<template>
  <div class="w-100">
    
    <!-- Mřížka s opraveným řádkovým odsazením karet (mb-4) -->
    <div class="row custom-stock-gap">
      <div 
        v-for="share in filteredRows" 
        :key="share.ISINID"
        class="col-12 col-md-6 mb-4"
      >           
        <div 
          @click="onRowClick(share.ISINID)" 
          class="border-0 shadow-sm p-4 h-100 rounded-4 d-flex flex-column justify-content-between option-card overflow-hidden"
          :class="isDarkMode ? 'option-card-dark' : 'bg-white option-card-light'"
          :style="{
            '--stock-color': share.Clr || '#64748b',
            cursor: 'pointer',
            backgroundColor: isDarkMode ? '#1E293B' : '#ffffff',
            border: isDarkMode ? '1px solid #334155' : '1px solid transparent',
            borderRadius: '0.65rem'
          }"
        >
          <!-- 1. ŘÁDEK: Logo v průhledném kroužku + Název/ISIN vlevo | 📈 DYNAMICKÁ VLNOVKA -->
          <div class="d-flex justify-content-between align-items-start mb-3 w-100">
            <div class="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
              
              <!-- KROUŽEK S LOGEM -->
              <div 
                class="d-flex align-items-center justify-content-center fw-bold share-logo-wrap" 
                :class="{ 'share-logo-fallback': !showShareLogo(share) }"
                :style="showShareLogo(share)
                  ? { width: '40px', height: '40px', backgroundColor: 'transparent', flexShrink: 0 }
                  : { 
                      width: '40px', 
                      height: '40px', 
                      backgroundColor: hexToRgbA(share.Clr || '#64748b', isDarkMode ? 0.22 : 0.12), 
                      color: share.Clr || '#64748b',
                      fontSize: '0.85rem',
                      flexShrink: 0,
                      borderRadius: '50%'
                    }"
              >
                <img
                  v-if="showShareLogo(share)"
                  :src="shareLogoUrl(share.ISIN)"
                  :alt="share.Nm"
                  class="share-logo-img"
                  @error="onLogoError(share.ISIN)"
                />
                <span v-else>{{ shareMonogram(share.Nm) }}</span>
              </div>

              <div class="min-w-0">
                <h6
                  class="fw-bold text-truncate mb-0"
                  :style="{ maxWidth: '100%', color: isDarkMode ? '#E2E8F0' : '#1E293B', fontSize: '1rem' }"
                >{{ share.Nm }}</h6>
                <span
                  class="small font-monospace d-block text-truncate"
                  :class="isDarkMode ? 'text-slate-400' : 'text-muted'"
                  style="font-size: 0.75rem;"
                >{{ share.ISIN }}</span>
              </div>
            </div>

            <div class="d-flex align-items-center justify-content-end ps-2 align-self-center gap-2" style="flex-shrink: 0;">
              <button
                type="button"
                class="forum-bubble-btn"
                :class="{ 'forum-bubble-btn-dark': isDarkMode }"
                :title="'Fórum: ' + share.Nm"
                @click.stop="openShareForum(share)"
              >
                <fa-icon icon="fa-regular fa-comment-dots" />
              </button>
              <div class="d-flex align-items-center justify-content-end" style="width: 70px; height: 24px; flex-shrink: 0;">
              <!-- A) STOUPAJÍCÍ TREND (PrcDfType === 1) -->
              <template v-if="share.PrcDfType === 1">
                <svg v-if="getMovementSize(share.PctDf) === 'big'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                  <path d="M2 18C12 18 15 12 22 8C29 4 36 2 48 2" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="getMovementSize(share.PctDf) === 'medium'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                  <path d="M2 16C10 15 15 10 23 9C31 8 38 5 48 3" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="60" height="20" viewBox="0 0 50 20" fill="none">
                  <path d="M2 12 C12 12 24 11 36 10 L48 9" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </template>

              <!-- B) KLESAJÍCÍ TREND (PrcDfType === -1) -->
              <template v-else-if="share.PrcDfType === -1">
                <svg v-if="getMovementSize(share.PctDf) === 'big'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                  <path d="M2 2 C12 2 15 8 22 12C29 16 36 18 48 18" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="getMovementSize(share.PctDf) === 'medium'" width="60" height="20" viewBox="0 0 50 20" fill="none">
                  <path d="M2 4 C10 5 15 10 23 11C31 12 38 15 48 17" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="60" height="20" viewBox="0 0 50 20" fill="none">
                  <path d="M2 8 C12 8 24 9 36 10 L48 11" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </template>

              <!-- C) STAGNACE (PrcDfType === 0) -->
              <svg v-else width="60" height="20" viewBox="0 0 50 20" fill="none">
                <path d="M2 10 C10 12 18 6 25 10C32 14 40 8 48 10" :stroke="isDarkMode ? '#64748B' : '#CBD5E1'" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              </div>
            </div>
          </div>

          <!-- 2. ŘÁDEK: Cena vlevo a předchozí závěr vpravo -->
          <div class="d-flex justify-content-between align-items-end mb-4">
            <div class="d-flex align-items-baseline gap-2">
              <h3
                class="fw-extrabold mb-0"
                :class="isDarkMode ? 'text-white' : 'text-dark'"
                style="letter-spacing: -0.5px;"
              >{{ formatFn(share.Prc) }} <span class="fs-6 fw-medium" :class="isDarkMode ? 'text-slate-400' : 'text-secondary'">Kč</span></h3>
              <i class="bi fs-5 lh-1" :class="share.PrcDfType === 1 ? 'bi-graph-up text-success' : (share.PrcDfType === -1 ? 'bi-graph-down text-danger' : (isDarkMode ? 'bi-dash-lg text-slate-400' : 'bi-dash-lg text-muted'))"></i>
            </div>
            
            <div class="d-flex flex-column align-items-end" :class="isDarkMode ? 'text-slate-400' : 'text-muted'" style="font-size: 0.75rem;">
              <span class="mb-1">předchozí závěr ({{ formatFn(share.LPrc) }})</span>
              <span
                class="badge px-2.5 py-1 rounded-pill fw-semibold small"
                :class="share.PrcDfType === 1
                  ? (isDarkMode ? 'badge-change-up-dark' : 'bg-success-light text-success')
                  : (share.PrcDfType === -1
                    ? (isDarkMode ? 'badge-change-down-dark' : 'bg-danger-light text-danger')
                    : (isDarkMode ? 'badge-change-flat-dark' : 'bg-light text-secondary'))"
              >
                {{ share.PrcDfType === 1 ? '+' : '' }}{{ share.PctDf }}%
              </span>
            </div>
          </div>

          <!-- 3. ŘÁDEK: Min-Max osa -->
          <div class="mt-2">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <span :class="isDarkMode ? 'text-slate-400' : 'text-muted'" style="font-size: 0.75rem;">90denní rozpětí:</span>
              
              <span 
                class="badge rounded-pill px-3 py-1.5 fw-bold d-inline-flex align-items-center gap-2 shadow-sm" 
                :style="{
                  backgroundColor: getPositionStatus(share.Prc, share.Mn, share.Mx).bgColor,
                  color: getPositionStatus(share.Prc, share.Mn, share.Mx).textColor,
                  fontSize: '0.7rem',
                  letterSpacing: '0.04em'
                }"
              >
                <span 
                  style="width: 7px; height: 7px; border-radius: 50%; display: inline-block; flex-shrink: 0;" 
                  :style="{ backgroundColor: getPositionStatus(share.Prc, share.Mn, share.Mx).dotColor }"
                ></span>
                {{ getPositionStatus(share.Prc, share.Mn, share.Mx).text }}
              </span>
            </div>

            <div
              class="position-relative w-100 my-2"
              :style="{ height: '4px', backgroundColor: isDarkMode ? '#0F172A' : '#F1F5F9', borderRadius: '2px' }"
            >
              <div class="position-absolute rounded-circle shadow-sm" :style="{ left: calculatePosition(share.Prc, share.Mn, share.Mx) + '%', top: '-4px', width: '12px', height: '12px', backgroundColor: 'var(--stock-color)', transform: 'translateX(-50%)', transition: 'left 0.3s ease' }"></div>
            </div>
            
            <div class="d-flex justify-content-between align-items-center mt-1" :class="isDarkMode ? 'text-slate-400' : 'text-muted'" style="font-size: 0.75rem;">
              <div>Min: <strong :class="share.MnDfType === 1 ? 'text-success' : (share.MnDfType === -1 ? 'text-danger' : (isDarkMode ? 'text-slate-200' : 'text-dark'))">{{ formatFn(share.Mn) }}</strong> <span class="ms-1" :class="share.MnDfType === 1 ? 'text-success' : (share.MnDfType === -1 ? 'text-danger' : (isDarkMode ? 'text-slate-400' : 'text-muted'))">({{ share.MnDf }})</span></div>
              <div>Max: <strong :class="share.MxDfType === 1 ? 'text-success' : (share.MxDfType === -1 ? 'text-danger' : (isDarkMode ? 'text-slate-200' : 'text-dark'))">{{ formatFn(share.Mx) }}</strong> <span class="ms-1" :class="share.MxDfType === 1 ? 'text-success' : (share.MxDfType === -1 ? 'text-danger' : (isDarkMode ? 'text-slate-400' : 'text-muted'))">({{ share.MxDf }})</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed } from "vue";
import { useStore } from "vuex";
import { shareLogoUrl, shareMonogram } from "@/helpers/shareLogos";

const props = defineProps({
  rows: Array,
  searchQuery: String
});

const store = useStore();
const isDarkMode = computed(() => store.state.isDarkMode);

const hexToRgbA = (hex, alpha) => {
  if (!hex) return `rgba(100, 116, 139, ${alpha})`;
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length == 3) { c = [c, c, c, c, c, c]; }
    c = '0x' + c.join('');
    return `rgba(${(c >> 16) & 255}, ${(c >> 8) & 255}, ${c & 255}, ${alpha})`;
  }
  return `rgba(100, 116, 139, ${alpha})`;
};

const logoFailed = reactive({});

const onLogoError = (isin) => {
  if (isin) logoFailed[isin] = true;
};

const showShareLogo = (share) => !!(shareLogoUrl(share.ISIN) && !logoFailed[share.ISIN]);

const filteredRows = computed(() => {
  const query = (props.searchQuery || "").trim().toLowerCase();
  if (!query) return props.rows || [];
  
  return (props.rows || []).filter(share => {
    const nameMatch = share.Nm ? share.Nm.toLowerCase().includes(query) : false;
    const isinMatch = share.ISIN ? share.ISIN.toLowerCase().includes(query) : false;
    return nameMatch || isinMatch;
  });
});

const onRowClick = (isinId) => {
  const targetShare = props.rows.find(s => s.ISINID === isinId);
  if (targetShare) store.dispatch("fetchISINChartData", targetShare);
};

const openShareForum = (share) => {
  if (!share?.ISINID) return;
  store.dispatch('openForum', {
    title: share.Nm || 'Akcie',
    ISINID: share.ISINID,
    ISIN: share.ISIN
  });
};

const formatFn = (val) => {
  if (!val && val !== 0) return "0";
  const num = parseFloat(String(val).replace(/\s+/g, '').replace(',', '.'));
  const hasDecimals = num % 1 !== 0;
  return num.toLocaleString('cs-CZ', { 
    minimumFractionDigits: hasDecimals ? 2 : 0, 
    maximumFractionDigits: 2 
  });
};

const calculatePosition = (current, min, max) => {
  const curNum = parseFloat(String(current).replace(/\s+/g, '').replace(',', '.'));
  const minNum = parseFloat(String(min).replace(/\s+/g, '').replace(',', '.'));
  const maxNum = parseFloat(String(max).replace(/\s+/g, '').replace(',', '.'));
  if (isNaN(curNum) || isNaN(minNum) || isNaN(maxNum) || maxNum === minNum) return 50;
  let pos = ((curNum - minNum) / (maxNum - minNum)) * 100;
  return Math.min(Math.max(pos, 0), 100);
};

const getPositionStatus = (current, min, max) => {
  const pos = calculatePosition(current, min, max);
  const dark = isDarkMode.value;
  if (pos >= 95) return dark
    ? { text: 'NA VRCHOLU', bgColor: 'rgba(52, 211, 153, 0.16)', textColor: '#34D399', dotColor: '#34D399' }
    : { text: 'NA VRCHOLU', bgColor: '#E6F4EA', textColor: '#137333', dotColor: '#15803D' };
  if (pos >= 65) return dark
    ? { text: 'POD VRCHOLEM', bgColor: 'rgba(52, 211, 153, 0.16)', textColor: '#34D399', dotColor: '#34D399' }
    : { text: 'POD VRCHOLEM', bgColor: '#E6F4EA', textColor: '#137333', dotColor: '#34A853' };
  if (pos >= 35) return dark
    ? { text: 'STŘED', bgColor: 'rgba(148, 163, 184, 0.14)', textColor: '#CBD5E1', dotColor: '#94A3B8' }
    : { text: 'STŘED', bgColor: '#F1F3F4', textColor: '#5F6368', dotColor: '#9AA0A6' };
  if (pos >= 5) return dark
    ? { text: 'NÍZKO', bgColor: 'rgba(248, 113, 113, 0.16)', textColor: '#F87171', dotColor: '#F87171' }
    : { text: 'NÍZKO', bgColor: '#FCE8E6', textColor: '#C5221F', dotColor: '#EA4335' };
  return dark
    ? { text: 'NA DNĚ', bgColor: 'rgba(248, 113, 113, 0.16)', textColor: '#F87171', dotColor: '#EF4444' }
    : { text: 'NA DNĚ', bgColor: '#FCE8E6', textColor: '#C5221F', dotColor: '#B91C1C' };
};

const getMovementSize = (pctDfStr) => {
  if (!pctDfStr) return 'small';
  const cleanStr = pctDfStr.replace('+', '').replace(',', '.');
  const pct = Math.abs(parseFloat(cleanStr));
  if (isNaN(pct)) return 'small';
  if (pct >= 4.5) return 'big';
  if (pct >= 1.0) return 'medium';
  return 'small';
};
</script>

<style scoped>
.text-slate-200 { color: #E2E8F0 !important; }
.text-slate-400 { color: #94A3B8 !important; }
.fw-extrabold { font-weight: 800; }
.border-dark-subtle { border-color: #1E293B !important; }

.share-logo-wrap {
  overflow: hidden;
}

.share-logo-img {
  width: 40px;
  height: 40px;
  object-fit: contain;
  display: block;
}

.share-logo-img-lg {
  width: 44px;
  height: 44px;
}

.option-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  border-radius: 0.65rem !important;
  overflow: hidden;
}

.option-card-light {
  background-color: #ffffff !important;
}

.option-card-dark {
  background-color: #1E293B !important;
  background-image: none !important;
  border: 1px solid #334155 !important;
  color: #E2E8F0;
}

.option-card-dark:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.28) !important;
  border-color: #334155 !important;
}

.badge-change-up-dark {
  background-color: rgba(52, 211, 153, 0.16) !important;
  color: #34D399 !important;
}

.badge-change-down-dark {
  background-color: rgba(248, 113, 113, 0.16) !important;
  color: #F87171 !important;
}

.badge-change-flat-dark {
  background-color: #0F172A !important;
  color: #94A3B8 !important;
}

.forum-bubble-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}

.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}

</style>

<style>
/* Argon .card drží bílé pozadí — bez třídy card a s globálním !important to přepíšeme. */
.option-card.option-card-dark {
  background-color: #1E293B !important;
  background-image: none !important;
  border: 1px solid #334155 !important;
  border-radius: 0.65rem !important;
}
</style>

